import { IGetCorrectionDocumentResponse } from '@/api/step-correction/type'
import { useDocumentUploader } from '@/hooks/useDocumentUploader'
import {
  IDocumentsFormValues,
  IUploaderProps,
  TUploadedFile
} from '@/types/uploader'
import { Alert } from '@dgshahr/ui-kit'
import FileUploader, { FileType } from '@dgshahr/ui-kit/Form/FileUploader'
import React from 'react'

const ACCEPT =
  'image/png, image/jpeg, image/jpg, image/PNG, image/JPEG, image/JPG , application/pdf'

const DESCRIPTION = 'فرمت‌های قابل قبول PNG , SVG یا PDF\nحداکثر حجم تا 5Mb'

const toSingleFile = (files: TUploadedFile[] | undefined) => {
  const file = files?.at(0)
  return file?.file || file?.src ? (file as FileType) : undefined
}

const NationalCardID = ({
  formik,
  isIndividual,
  corrections
}: IUploaderProps) => {
  const { values, errors, submitCount } = formik
  const { handleUpload, handleDelete } = useDocumentUploader(formik)

  const descriptions = [
    corrections?.NATIONAL_ID_CARD,
    corrections?.NATIONAL_ID_CARD_BACK_SIDE,
    corrections?.BIRTH_CERTIFICATE
  ].filter(Boolean)

  const fieldError = (field: keyof IDocumentsFormValues) =>
    submitCount > 0 ? (errors[field] as string | undefined) : undefined

  const handleNationalIDFrontChange = (file: File | undefined) =>
    file && handleUpload('NATIONAL_ID_CARD', file)

  const handleNationalIDBackChange = (file: File | undefined) =>
    file && handleUpload('NATIONAL_ID_CARD_BACK_SIDE', file)

  const handleBirthCertificateChange = (file: File | undefined) =>
    file && handleUpload('BIRTH_CERTIFICATE', file)

  const handleDeleteFile = (
    fieldName: keyof IDocumentsFormValues,
    e: TUploadedFile
  ): void => {
    handleDelete(fieldName, e)
  }

  return (
    <div className="flex flex-col gap-8">
      {Boolean(descriptions?.length) && (
        <div className="flex flex-col gap-4">
          <Alert
            size="small"
            title="اصلاحیه‌"
            variant="error"
            text={descriptions?.map((desc: IGetCorrectionDocumentResponse) => (
              <React.Fragment key={desc.id}>
                {desc.comment}
                <br />
              </React.Fragment>
            ))}
          />
        </div>
      )}
      <label
        htmlFor="national-id-front"
        className="font-p2-medium text-gray-500"
      >
        تصویری از رو و پشت کارت ملی و صفحه اول شناسنامه فرد صاحب پروانه کسب
        بارگذاری کنید.
        <span className="text-error-500"> *</span>
      </label>
      <div className="flex flex-col tablet:flex-row gap-4">
        <FileUploader
          disabled={values.NATIONAL_ID_CARD.length >= 1}
          className="w-fit"
          isError={Boolean(fieldError('NATIONAL_ID_CARD'))}
          fileInputProps={{
            id: 'national-id-front',
            className: 'ss02',
            required: true,
            description: DESCRIPTION,
            helperProps: {
              errorMessage: fieldError('NATIONAL_ID_CARD')
            },
            title: 'تصویر روی کارت ملی',
            accept: ACCEPT
          }}
          mode="single"
          files={toSingleFile(values.NATIONAL_ID_CARD)}
          onChange={handleNationalIDFrontChange}
          previewProps={{
            leftButton: {
              onClick: (e) =>
                handleDeleteFile('NATIONAL_ID_CARD', e as TUploadedFile)
            },
            rightButton: false
          }}
        />

        <FileUploader
          disabled={values.NATIONAL_ID_CARD_BACK_SIDE.length >= 1}
          className="w-fit"
          isError={Boolean(fieldError('NATIONAL_ID_CARD_BACK_SIDE'))}
          fileInputProps={{
            id: 'national-id-back',
            className: 'ss02',
            required: true,
            description: DESCRIPTION,
            helperProps: {
              errorMessage: fieldError('NATIONAL_ID_CARD_BACK_SIDE')
            },
            title: 'تصویر پشت کارت ملی',
            accept: ACCEPT
          }}
          mode="single"
          files={toSingleFile(values.NATIONAL_ID_CARD_BACK_SIDE)}
          onChange={handleNationalIDBackChange}
          previewProps={{
            leftButton: {
              onClick: (e) =>
                handleDeleteFile(
                  'NATIONAL_ID_CARD_BACK_SIDE',
                  e as TUploadedFile
                )
            },
            rightButton: false
          }}
        />

        <FileUploader
          disabled={isIndividual && values.BIRTH_CERTIFICATE.length >= 1}
          className="w-fit"
          isError={Boolean(fieldError('BIRTH_CERTIFICATE'))}
          fileInputProps={{
            id: 'birth-certificate',
            className: 'ss02',
            required: true,
            description: DESCRIPTION,
            helperProps: {
              errorMessage: fieldError('BIRTH_CERTIFICATE')
            },
            title: 'تصویر صفحه اول شناسنامه',
            accept: ACCEPT
          }}
          mode="single"
          files={toSingleFile(values.BIRTH_CERTIFICATE)}
          onChange={handleBirthCertificateChange}
          previewProps={{
            leftButton: {
              onClick: (e) =>
                handleDeleteFile('BIRTH_CERTIFICATE', e as TUploadedFile)
            },
            rightButton: false
          }}
        />
      </div>
    </div>
  )
}

export default NationalCardID
